import type { ReactElement } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Spinner } from "./Spinner";
import { useClinicianMe } from "../hooks/useClinicianMe";

interface Props {
  children: ReactElement;
}

/**
 * Wrapper que se monta DENTRO de RequireAuth.
 *
 * Flujo:
 *   1. Loading → spinner centrado
 *   2. Error → mensaje + botón Reintentar (no auto-logout, eso lo hace
 *      RequireAuth si el error es 401 con ApiError UnauthorizedError;
 *      acá llegamos solo con errores de red u otros)
 *   3. needs_specialty_confirmation === true → redirect a la página
 *      de confirmación. Preserva la ruta actual en `state.from` para
 *      que el form pueda volver al sitio original al guardar.
 *   4. Caso feliz → render children
 *
 * Importante: la ruta /console/account/specialty NO debe envolverse
 * con este wrapper, solo con RequireAuth — sino entra en loop
 * (needs_confirmation → redirect → needs_confirmation → ...).
 */
export function RequireSpecialtyConfirmed({ children }: Props) {
  const me = useClinicianMe();
  const location = useLocation();

  if (me.isLoading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: "#0a0e1a",
        }}
      >
        <Spinner size={32} label="Cargando perfil..." />
      </div>
    );
  }

  if (me.isError) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: "#0a0e1a",
          color: "#e5e7eb",
          padding: 24,
        }}
      >
        <div style={{ textAlign: "center", maxWidth: 380 }}>
          <p style={{ fontSize: 18, marginBottom: 12, fontWeight: 500 }}>
            No pudimos cargar tu perfil
          </p>
          <p
            style={{
              fontSize: 14,
              color: "#94a3b8",
              marginBottom: 20,
              lineHeight: 1.5,
            }}
          >
            Verificá tu conexión y reintentá. Si el problema persiste,
            cerrá sesión y volvé a ingresar tu identificador.
          </p>
          <button
            onClick={() => me.refetch()}
            style={{
              padding: "10px 20px",
              background: "#00A3E0",
              color: "white",
              border: 0,
              borderRadius: 6,
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (me.data?.needs_specialty_confirmation) {
    return (
      <Navigate
        to="/console/account/specialty"
        replace
        state={{ from: location.pathname }}
      />
    );
  }

  return children;
}
